import { useCallback, useEffect, useState } from 'react';
import {
  ArrowRight,
  Eye,
  RefreshCw,
  Search,
  Trash2,
  X,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

type ContactMessage = {
  id: number;
  name: string;
  phone: string;
  email: string;
  message: string;
  status: string;
  createdAt: string;
  repliedAt: string | null;
  adminNotes: string | null;
};

const statusLabels: Record<string, string> = {
  new: 'جديدة',
  in_progress: 'قيد المتابعة',
  replied: 'تم الرد',
  closed: 'مغلقة',
};

const statusColors: Record<string, string> = {
  new: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-yellow-100 text-yellow-700',
  replied: 'bg-green-100 text-green-700',
  closed: 'bg-gray-100 text-gray-700',
};

export default function ContactMessagesPage() {
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [selected, setSelected] = useState<ContactMessage | null>(null);
  const [saving, setSaving] = useState(false);

  const getToken = () =>
    localStorage.getItem('token') ||
    localStorage.getItem('accessToken') ||
    '';

  const loadMessages = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams();

      if (status) {
        params.set('status', status);
      }

      if (search.trim()) {
        params.set('search', search.trim());
      }

      const query = params.toString();
      const url = query
        ? `/api/Contact?${query}`
        : '/api/Contact';

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(
          data?.message ||
            data?.title ||
            'تعذر تحميل رسائل التواصل'
        );
      }

      setMessages(data?.data || []);
    } catch (err) {
      console.error(err);
      setError(
        err instanceof Error
          ? err.message
          : 'تعذر تحميل رسائل التواصل'
      );
    } finally {
      setLoading(false);
    }
  }, [search, status]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  const updateStatus = async (
    id: number,
    newStatus: string,
    adminNotes?: string
  ) => {
    setSaving(true);
    setError('');

    try {
      const response = await fetch(
        `/api/Contact/${id}/status`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify({
            status: newStatus,
            adminNotes,
          }),
        }
      );

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(
          data?.message ||
            data?.title ||
            'تعذر تحديث الرسالة'
        );
      }

      setSelected((current) =>
        current
          ? {
              ...current,
              status: newStatus,
              adminNotes:
                adminNotes !== undefined
                  ? adminNotes
                  : current.adminNotes,
              repliedAt:
                newStatus === 'replied'
                  ? current.repliedAt || new Date().toISOString()
                  : current.repliedAt,
            }
          : current
      );

      await loadMessages();
    } catch (err) {
      console.error(err);
      setError(
        err instanceof Error
          ? err.message
          : 'تعذر تحديث الرسالة'
      );
    } finally {
      setSaving(false);
    }
  };

  const deleteMessage = async (id: number) => {
    if (!window.confirm('هل أنت متأكد من حذف هذه الرسالة؟')) {
      return;
    }

    setSaving(true);
    setError('');

    try {
      const response = await fetch(`/api/Contact/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(
          data?.message ||
            data?.title ||
            'تعذر حذف الرسالة'
        );
      }

      setSelected(null);
      await loadMessages();
    } catch (err) {
      console.error(err);
      setError(
        err instanceof Error
          ? err.message
          : 'تعذر حذف الرسالة'
      );
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (value: string) => {
    try {
      return new Intl.DateTimeFormat('ar-EG', {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(value));
    } catch {
      return value;
    }
  };

  return (
    <div className="p-6 space-y-6" dir="rtl">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            رسائل اتصل بنا
          </h1>
          <p className="text-gray-500 mt-1">
            <div className="mb-4">
        <Button
          variant="outline"
          onClick={() => window.location.href = '/dashboard'}
          className="gap-2"
        >
          <ArrowRight className="h-4 w-4" />
          العودة إلى لوحة التحكم
        </Button>
      </div>
      إدارة الرسائل الواردة من نموذج التواصل في الموقع.
          </p>
        </div>

        <Button
          variant="outline"
          onClick={loadMessages}
          disabled={loading}
        >
          <RefreshCw className="w-4 h-4 ml-2" />
          تحديث
        </Button>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="relative">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="بحث بالاسم أو الهاتف أو البريد..."
                className="pr-10"
              />
            </div>

            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="h-10 rounded-md border border-gray-200 bg-white px-3 text-sm"
            >
              <option value="">كل الحالات</option>
              <option value="new">جديدة</option>
              <option value="in_progress">قيد المتابعة</option>
              <option value="replied">تم الرد</option>
              <option value="closed">مغلقة</option>
            </select>

            <div className="flex items-center text-sm text-gray-500">
              إجمالي الرسائل: {messages.length}
            </div>
          </div>
        </CardContent>
      </Card>

      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>الرسائل</CardTitle>
        </CardHeader>

        <CardContent>
          {loading ? (
            <div className="py-12 text-center text-gray-500">
              جارٍ تحميل الرسائل...
            </div>
          ) : messages.length === 0 ? (
            <div className="py-12 text-center text-gray-500">
              لا توجد رسائل.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="p-3 text-right">الاسم</th>
                    <th className="p-3 text-right">الهاتف</th>
                    <th className="p-3 text-right">البريد</th>
                    <th className="p-3 text-right">الحالة</th>
                    <th className="p-3 text-right">التاريخ</th>
                    <th className="p-3 text-right">إجراء</th>
                  </tr>
                </thead>

                <tbody>
                  {messages.map((item) => (
                    <tr
                      key={item.id}
                      className="border-b hover:bg-gray-50"
                    >
                      <td className="p-3 font-medium">
                        {item.name}
                      </td>

                      <td className="p-3" dir="ltr">
                        {item.phone}
                      </td>

                      <td className="p-3">
                        {item.email}
                      </td>

                      <td className="p-3">
                        <span
                          className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${
                            statusColors[item.status] ||
                            statusColors.new
                          }`}
                        >
                          {statusLabels[item.status] ||
                            item.status}
                        </span>
                      </td>

                      <td className="p-3 text-gray-500">
                        {formatDate(item.createdAt)}
                      </td>

                      <td className="p-3">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setSelected(item)}
                        >
                          <Eye className="w-4 h-4 ml-1" />
                          عرض
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b">
              <div>
                <h2 className="text-xl font-bold">
                  تفاصيل الرسالة
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  رقم الرسالة: #{selected.id}
                </p>
              </div>

              <button
                type="button"
                onClick={() => setSelected(null)}
                className="p-2 rounded-md hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 mb-1">
                    الاسم
                  </p>
                  <p className="font-semibold">
                    {selected.name}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-gray-500 mb-1">
                    الهاتف
                  </p>
                  <p className="font-semibold" dir="ltr">
                    {selected.phone}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-gray-500 mb-1">
                    البريد الإلكتروني
                  </p>
                  <p className="font-semibold">
                    {selected.email}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-gray-500 mb-1">
                    تاريخ الإرسال
                  </p>
                  <p className="font-semibold">
                    {formatDate(selected.createdAt)}
                  </p>
                </div>
              </div>

              <div>
                <p className="text-xs text-gray-500 mb-2">
                  الرسالة
                </p>
                <div className="rounded-lg bg-gray-50 border p-4 whitespace-pre-wrap leading-7">
                  {selected.message}
                </div>
              </div>

              <div>
                <p className="text-sm font-semibold mb-2">
                  تغيير الحالة
                </p>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {Object.entries(statusLabels).map(
                    ([value, label]) => (
                      <Button
                        key={value}
                        type="button"
                        variant={
                          selected.status === value
                            ? 'default'
                            : 'outline'
                        }
                        disabled={saving}
                        onClick={() =>
                          updateStatus(
                            selected.id,
                            value,
                            selected.adminNotes || undefined
                          )
                        }
                        className={
                          selected.status === value
                            ? 'bg-orange-600 hover:bg-orange-700'
                            : ''
                        }
                      >
                        {label}
                      </Button>
                    )
                  )}
                </div>
              </div>

              {selected.adminNotes && (
                <div>
                  <p className="text-xs text-gray-500 mb-1">
                    ملاحظات الإدارة
                  </p>
                  <div className="rounded-lg bg-yellow-50 border border-yellow-200 p-3">
                    {selected.adminNotes}
                  </div>
                </div>
              )}

              <div className="flex justify-between gap-3 pt-3 border-t">
                <Button
                  type="button"
                  variant="destructive"
                  disabled={saving}
                  onClick={() => deleteMessage(selected.id)}
                >
                  <Trash2 className="w-4 h-4 ml-2" />
                  حذف الرسالة
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setSelected(null)}
                >
                  إغلاق
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
